import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { LogoMark } from "@/components/Logo";
import { toast } from "sonner";

export default function LoginPage() {
  const { loginWithPassword, register } = useAuth();
  const { t } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from || "/chat";

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "login") {
        await loginWithPassword(email, password);
        toast.success("Welcome back to the tavern.");
      } else {
        await register(email, password, name);
        toast.success("Your seat at the tavern is reserved.");
      }
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md tavern-card p-8 sm:p-10 relative overflow-hidden" data-testid="login-card">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-crimson/10 blur-3xl" />
        <div className="flex flex-col items-center text-center gap-3 mb-8">
          <LogoMark size={56} />
          <h1 className="font-display text-2xl font-bold tracking-[0.3em] uppercase text-white">
            {mode === "login" ? t("login.title") : t("register.title")}
          </h1>
          <p className="text-sm text-zinc-500 max-w-xs">
            {mode === "login" ? t("login.subtitle") : t("register.subtitle")}
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
          {mode === "register" && (
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Username (shown in chat)</label>
              <input
                data-testid="register-username"
                pattern="[A-Za-z0-9_\-]{3,20}"
                title="3-20 letters, numbers, _ or -"
                minLength={3}
                className="tavern-input w-full px-3 py-2.5 rounded-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={20}
                placeholder="Grimhollow"
              />
            </div>
          )}
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Email</label>
            <input
              data-testid="login-email"
              type="email"
              className="tavern-input w-full px-3 py-2.5 rounded-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@realm.com"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Password</label>
            <input
              data-testid="login-password"
              type="password"
              className="tavern-input w-full px-3 py-2.5 rounded-sm"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="btn-crimson w-full py-2.5 text-xs tracking-[0.3em] uppercase font-semibold disabled:opacity-60"
          >
            {submitting ? "..." : mode === "login" ? t("login.button") : t("register.button")}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">or</span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>

        <button
          onClick={onGoogle}
          data-testid="google-login"
          className="btn-ghost w-full py-2.5 text-xs tracking-[0.3em] uppercase font-semibold"
        >
          Continue with Google
        </button>

        <div className="mt-6 text-center text-xs text-zinc-500">
          {mode === "login" ? (
            <>
              First visit?{" "}
              <button data-testid="switch-register" className="text-crimson-bright hover:underline" onClick={() => setMode("register")}>
                Reserve a seat
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button data-testid="switch-login" className="text-crimson-bright hover:underline" onClick={() => setMode("login")}>
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
